import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Search, Trash2, ArrowLeft, Phone, Mail, AlertCircle } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { Card, ClinicaLabel, EstadoBadge, formatDate, MarcacaoLink, PageTitle } from "../ui";
import { listarClientes, detalheCliente, apagarCliente } from "../../lib/adminP1Api";
import { useAuth } from "../AuthContext";

export function AdminClientesPage() {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    setData(await listarClientes(q ? { q } : {}));
    setLoading(false);
  }, [q]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div data-testid="admin-clientes">
      <PageTitle subtitle="Pesquisar e gerir tutores.">Clientes</PageTitle>
      <Card className="mb-5">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A5A5A]" />
          <input
            id="admin-search"
            data-testid="cli-search"
            placeholder="Procurar por nome, email, telefone ou NIF... (atalho: /)"
            defaultValue={q}
            onKeyDown={(e) => { if (e.key === "Enter") setQ(e.target.value); }}
            className="w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 pl-10 pr-3 py-2.5 text-sm focus:border-[#1E5F6E] focus:outline-none"
          />
        </div>
      </Card>
      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-xs uppercase tracking-wider text-[#5A5A5A]">
              <tr>
                <th className="text-left py-3 px-4">Nome</th>
                <th className="text-left py-3 px-4">Contactos</th>
                <th className="text-left py-3 px-4">Animais</th>
                <th className="text-left py-3 px-4">Marcações</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="py-6 text-center text-[#5A5A5A]">A carregar...</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-[#5A5A5A]">Sem clientes.</td></tr>}
              {!loading && data.items.map((c) => (
                <tr key={c.id} className="border-t border-[#1E5F6E]/10 hover:bg-[#FAF7F2]" data-testid={`cli-${c.id}`}>
                  <td className="py-3 px-4">
                    <Link to={`/admin/clientes/${c.id}`} className="font-semibold text-[#1E5F6E] hover:underline">{c.nome}</Link>
                  </td>
                  <td className="py-3 px-4 text-xs">
                    <div>{c.email}</div>
                    <div className="text-[#5A5A5A]">{c.telefone}</div>
                  </td>
                  <td className="py-3 px-4">{c._animais}</td>
                  <td className="py-3 px-4">{c._marcacoes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export function AdminClienteDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const carregar = async () => setData(await detalheCliente(id));
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [id]);

  const onDelete = async () => {
    setDeleting(true);
    try {
      await apagarCliente(id);
      toast.success("Cliente apagado e dados anonimizados nas marcações.");
      navigate("/admin/clientes");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao apagar.");
    } finally { setDeleting(false); }
  };

  if (!data) return <p className="text-[#5A5A5A]">A carregar...</p>;
  const c = data.cliente;
  return (
    <div data-testid="admin-cliente-detail">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-bold text-[#1E5F6E] mb-4">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>
      <PageTitle subtitle={c.email}>{c.nome}</PageTitle>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card testId="card-cli-info" className="lg:col-span-2">
          <h2 className="font-display font-bold text-lg mb-3">Contactos</h2>
          <div className="text-sm space-y-2">
            <div><strong>Email:</strong> <a href={`mailto:${c.email}`} className="text-[#1E5F6E]">{c.email}</a></div>
            <div><strong>Telefone:</strong> <a href={`tel:${c.telefone}`} className="text-[#1E5F6E]">{c.telefone}</a></div>
            {c.nif && <div><strong>NIF:</strong> {c.nif}</div>}
            <div className="text-xs text-[#5A5A5A]">Criado em {formatDate(c.created_at)}</div>
          </div>
        </Card>
        <Card testId="card-cli-animais">
          <h2 className="font-display font-bold text-lg mb-3">Animais ({data.animais.length})</h2>
          {data.animais.length === 0 ? <p className="text-sm text-[#5A5A5A]">Sem animais registados.</p> : (
            <ul className="text-sm divide-y divide-[#1E5F6E]/10">
              {data.animais.map((a) => (
                <li key={a.id} className="py-2">
                  <div className="font-bold">{a.nome}</div>
                  <div className="text-xs text-[#5A5A5A] capitalize">{a.especie}{a.raca ? ` — ${a.raca}` : ""}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="lg:col-span-3" testId="card-cli-historico">
          <h2 className="font-display font-bold text-lg mb-3">Histórico de marcações ({data.marcacoes.length})</h2>
          {data.marcacoes.length === 0 ? <p className="text-sm text-[#5A5A5A]">Nenhuma marcação.</p> : (
            <ul className="divide-y divide-[#1E5F6E]/10 text-sm">
              {data.marcacoes.map((m) => (
                <li key={m.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div>
                    <MarcacaoLink id={m.id}>{m.numero_marcacao}</MarcacaoLink>
                    <span className="text-[#5A5A5A] ml-2">{formatDate(m.data_hora)} · <ClinicaLabel slug={m.clinica_slug} /></span>
                  </div>
                  <EstadoBadge estado={m.estado} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {isAdmin && (
        <Card className="mt-6 border-rose-200 bg-rose-50/50" testId="card-rgpd">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-rose-600 mt-0.5" />
            <div>
              <h3 className="font-display font-bold text-rose-700">Zona perigosa — RGPD</h3>
              <p className="text-sm text-[#5A5A5A] mt-1">
                Apagar o cliente remove os dados pessoais e anonimiza as marcações (para contabilidade). Esta ação é <strong>irreversível</strong>.
              </p>
              <button data-testid="btn-rgpd-delete" onClick={() => setModalOpen(true)} className="mt-3 inline-flex items-center gap-2 rounded-full bg-rose-600 text-white px-5 py-2.5 text-sm font-bold hover:bg-rose-700">
                <Trash2 className="h-4 w-4" />
                Apagar cliente
              </button>
            </div>
          </div>
        </Card>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setModalOpen(false)} data-testid="modal-delete">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold mb-2">Confirmar eliminação?</h3>
            <p className="text-sm text-[#5A5A5A] mb-4">Para confirmar, escreva <strong>{c.email}</strong> abaixo.</p>
            <input
              data-testid="confirm-text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full rounded-xl border-2 border-rose-200 px-3 py-2.5 text-sm"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setModalOpen(false)} className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-4 py-2 text-sm font-bold text-[#1E5F6E]">Cancelar</button>
              <button onClick={onDelete} disabled={confirmText !== c.email || deleting} data-testid="confirm-delete" className="rounded-full bg-rose-600 text-white px-4 py-2 text-sm font-bold disabled:opacity-40">
                {deleting ? "A apagar..." : "Apagar para sempre"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
